/**
 * AddonsSection — lista los add-ons disponibles para el plan actual y permite
 * activarlos/desactivarlos.
 *
 * Contrato backend (autoritativo):
 *   GET  /addons?planId={planId}            -> { data: AddOn[] }
 *   POST /subscriptions/{subId}/addons      -> { addOnId, action: 'activate'|'deactivate' }
 *
 * AddOn = { addOnId, name, description, pricing: { USD: { monthly } },
 *           availableFrom, featureFlags, limitsModifier, active }
 *
 * El estado "activo" se deriva de subscription.limits (featureFlags ⊆ limits).
 * Una feature incluida nativamente por el plan (no comprada como add-on) se
 * muestra como "Incluido en tu plan" y no se puede togglear desde acá.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getAddons, toggleAddon } from '../services/api';
import { formatPriceWithCurrency } from '../utils/formatCurrency';
import { classifyAddons, getAvailableAddOnIds, withTierFeatureCards } from '../config/featureGates';
import './AddonsSection.css';

const ADDON_ICONS = {
  'afip-invoicing': '🧾',
  delivery: '🚚',
  'customer-app': '📱',
  'extra-restaurant': '🏪',
  'advanced-inventory': '📦',
};

function AddonsSection({
  subscriptionId,
  currentPlanId,
  availableAddOns,
  currency = 'ARS',
  subscriptionLimits,
  variant = 'dark',
  onAddonChange,
}) {
  const { t } = useTranslation();
  const [addons, setAddons] = useState([]);
  const [limits, setLimits] = useState(subscriptionLimits || {});
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [error, setError] = useState('');

  // Keep local limits in sync if the parent reloads the subscription.
  useEffect(() => {
    setLimits(subscriptionLimits || {});
  }, [subscriptionLimits]);

  const loadAddons = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getAddons(currentPlanId).catch(() => ({ data: [] }));
      setAddons(result.data || []);
    } catch {
      setAddons([]);
    } finally {
      setLoading(false);
    }
  }, [currentPlanId]);

  useEffect(() => {
    loadAddons();
  }, [loadAddons]);

  // ¿El add-on está activo (comprado)? Todas sus featureFlags están en limits.
  const isAddonActive = (addon) => {
    const flags = Object.keys(addon.featureFlags || {});
    if (flags.length === 0) return false; // ej: extra-restaurant (solo limitsModifier)
    return flags.every((flag) => limits?.[flag] === true);
  };

  const handleToggle = async (addon) => {
    if (!subscriptionId || toggling) return;
    setError('');
    const activate = !isAddonActive(addon);
    try {
      setToggling(addon.addOnId);
      const result = await toggleAddon(subscriptionId, addon.addOnId, activate);
      // El backend devuelve los limits actualizados — usarlos como fuente de verdad.
      if (result?.subscription?.limits) {
        setLimits(result.subscription.limits);
      }
      if (onAddonChange) onAddonChange();
    } catch (err) {
      console.error('Error toggling addon:', err);
      setError(err.response?.data?.message || t('planSelection.addons.toggleError'));
    } finally {
      setToggling(null);
    }
  };

  if (loading) return null;

  // Clasificación por plan: add-ons activables (incluye AFIP en Starter, según
  // plan.availableAddOns del backend o el fallback local), features incluidas
  // por el plan (no toggleables) y features que requieren subir de plan.
  const {
    sellable: realAddons,
    included: includedFeatures,
    upgrade: upgradeFeatures,
  } = classifyAddons(
    // Delivery, Customer App y recetas ya no vienen en el catálogo (add-ons
    // desactivados): se suman localmente para listarlas como incluidas.
    withTierFeatureCards(addons),
    currentPlanId,
    getAvailableAddOnIds(currentPlanId, { availableAddOns })
  );

  if (realAddons.length === 0 && includedFeatures.length === 0 && upgradeFeatures.length === 0) {
    return null;
  }

  const renderFeatureCard = ({ addon, requiredPlan }, kind) => {
    return (
      <div key={addon.addOnId} className={`addon-card addon-feature addon-feature--${kind}`}>
        <div className="addon-header">
          <span className="addon-icon">{ADDON_ICONS[addon.addOnId] || '🔧'}</span>
          <div className="addon-info">
            <h3 className="addon-name">
              {t(`planSelection.addons.items.${addon.addOnId}.name`, addon.name)}
            </h3>
            <p className="addon-description">
              {t(`planSelection.addons.items.${addon.addOnId}.description`, addon.description)}
            </p>
          </div>
        </div>
        <div className="addon-footer">
          {kind === 'included' ? (
            <span className="addon-included-label">
              ✅ {t('planSelection.addons.includedBadge')}
            </span>
          ) : (
            <span className="addon-upgrade-label">
              🔓{' '}
              {t('planSelection.addons.availableInPlan', {
                plan: t(`planSelection.plans.${requiredPlan}.name`, requiredPlan),
              })}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`addons-section addons-section--${variant}`}>
      {error && <div className="addons-error">{error}</div>}

      {/* Add-ons activables desde el plan actual (AFIP en Starter, Restaurante adicional) */}
      {realAddons.length > 0 && (
        <>
          <h2 className="addons-title">{t('planSelection.addons.title')}</h2>
          <p className="addons-subtitle">{t('planSelection.addons.subtitle')}</p>
          <div className="addons-grid">
            {realAddons.map((addon) => {
              // Las features de tier ya incluidas se muestran abajo; acá todo es activable.
              const isActive = isAddonActive(addon);
              const pricing = addon.pricing?.[currency] || addon.pricing?.ARS || {};
              const isBusy = toggling === addon.addOnId;

              return (
                <div key={addon.addOnId} className={`addon-card ${isActive ? 'addon-active' : ''}`}>
                  <div className="addon-header">
                    <span className="addon-icon">{ADDON_ICONS[addon.addOnId] || '🔧'}</span>
                    <div className="addon-info">
                      <h3 className="addon-name">
                        {t(`planSelection.addons.items.${addon.addOnId}.name`, addon.name)}
                      </h3>
                      <p className="addon-description">
                        {t(
                          `planSelection.addons.items.${addon.addOnId}.description`,
                          addon.description
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="addon-footer">
                    <span className="addon-price">
                      {pricing.monthly != null
                        ? `${formatPriceWithCurrency(pricing.monthly, currency)}/${t('planSelection.month')}`
                        : t('planSelection.contactUs')}
                    </span>
                    <button
                      type="button"
                      className={`btn ${isActive ? 'btn-secondary' : 'btn-primary'} btn-sm`}
                      onClick={() => handleToggle(addon)}
                      disabled={isBusy || !subscriptionId}
                    >
                      {isBusy
                        ? t('planSelection.processing')
                        : isActive
                          ? t('planSelection.addons.deactivate')
                          : t('planSelection.addons.activate')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Features incluidas en el plan actual (no toggleables) */}
      {includedFeatures.length > 0 && (
        <>
          <h2 className="addons-title">{t('planSelection.addons.planFeaturesTitle')}</h2>
          <p className="addons-subtitle">{t('planSelection.addons.planFeaturesSubtitle')}</p>
          <div className="addons-grid">
            {includedFeatures.map((item) => renderFeatureCard(item, 'included'))}
          </div>
        </>
      )}

      {/* Features que se desbloquean al subir de plan */}
      {upgradeFeatures.length > 0 && (
        <>
          <h2 className="addons-title">{t('planSelection.addons.upgradeTitle')}</h2>
          <p className="addons-subtitle">{t('planSelection.addons.upgradeSubtitle')}</p>
          <div className="addons-grid">
            {upgradeFeatures.map((item) => renderFeatureCard(item, 'upgrade'))}
          </div>
        </>
      )}
    </div>
  );
}

export default AddonsSection;
